import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Checkbox, Radio, Avatar, Spin, Empty, Tag } from "antd";
import { getUserList, useMetadataStore } from "@ifa/utils";
import {
  IdcardOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { MemberListProps, MemberListRef, Member } from "./types";
import {
  ACCOUNT_STATUS_LABELS,
  EMPTY_TEXTS,
  EMPLOYMENT_STATUS_LABELS,
} from "./utils/constants";
import { normalizeMember } from "./utils/normalizeMember";
import { buildMemberListFetchParams } from "./utils/filters";
import { getPersonnelTypeLabel } from "./utils/personnelType";
import styles from "./MemberList.module.css";

const tagStyleMap: Record<string, string> = {
  IFA: styles.tagPrimary,
  INTERNAL_STAFF: styles.tagNeutral,
  REFERRER: styles.tagNeutral,
  ENABLED: styles.tagPrimary,
  DISABLED: styles.tagNeutral,
  DELETED: styles.tagMuted,
};

const MemberList = forwardRef<MemberListRef, MemberListProps>((props, ref) => {
  const selectMetadataList = useMetadataStore(
    (state) => state.selectMetadataList
  );

  const {
    teamId,
    searchKeyword,
    filters,
    pageSize,
    mode,
    selectedMemberIds,
    maxCount,
    disabledMemberIds,
    extraParams,
    onMemberSelect,
    onMemberDeselect,
    memberListFetcher,
  } = props;

  const listContainerRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<Member[]>([]);
  const pageNumRef = useRef(1);
  const loadingRef = useRef(false);

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const syncMembersState = useCallback(
    (nextMembers: Member[], nextPageNum: number, nextHasMore: boolean) => {
      membersRef.current = nextMembers;
      pageNumRef.current = nextPageNum;
      setMembers(nextMembers);
      setHasMore(nextHasMore);
    },
    []
  );

  const loadData = useCallback(
    async (resetPage: boolean = false) => {
      if (loadingRef.current) {
        return;
      }

      const currentPage = resetPage ? 1 : pageNumRef.current;
      loadingRef.current = true;
      setLoading(true);

      try {
        const params = buildMemberListFetchParams({
          pageNum: currentPage,
          pageSize,
          teamId,
          searchKeyword,
          filters,
          extraParams,
        });

        const response = memberListFetcher
          ? await memberListFetcher(params)
          : await getUserList(params);

        const formattedMembers: Member[] = (response.content || []).map(
          normalizeMember
        );
        const totalPagesCount =
          response.page?.totalPages ?? response.totalPages ?? 0;
        const mergedMembers = resetPage
          ? formattedMembers
          : [...membersRef.current, ...formattedMembers];
        const nextPageNum =
          formattedMembers.length > 0 ? currentPage + 1 : currentPage;

        syncMembersState(
          mergedMembers,
          nextPageNum,
          currentPage < totalPagesCount
        );
      } catch (error) {
        console.error("[MemberList] 載入成員失敗:", error);
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [
      extraParams,
      filters,
      memberListFetcher,
      pageSize,
      searchKeyword,
      syncMembersState,
      teamId,
    ]
  );

  const resetAndLoad = useCallback(() => {
    syncMembersState([], 1, true);
    void loadData(true);
  }, [loadData, syncMembersState]);

  useImperativeHandle(
    ref,
    () => ({
      reload: resetAndLoad,
      loadMore: () => {
        void loadData(false);
      },
    }),
    [loadData, resetAndLoad]
  );

  useEffect(() => {
    resetAndLoad();
  }, [resetAndLoad]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const distanceToBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (distanceToBottom < 80 && hasMore && !loadingRef.current) {
        void loadData(false);
      }
    },
    [hasMore, loadData]
  );

  const isMaxReached = useMemo(() => {
    return maxCount !== undefined && selectedMemberIds.size >= maxCount;
  }, [maxCount, selectedMemberIds.size]);

  const handleSelectChange = useCallback(
    (member: Member, checked: boolean) => {
      if (checked) {
        onMemberSelect(member);
        return;
      }

      onMemberDeselect(member.uid);
    },
    [onMemberDeselect, onMemberSelect]
  );

  const renderMetaTags = (member: Member) => {
    const personnelTypeLabel = getPersonnelTypeLabel(
      member.personnelType,
      selectMetadataList
    );

    return (
      <div className={styles.metaTags}>
        {personnelTypeLabel ? (
          <Tag
            bordered={false}
            className={`${styles.metaTag} ${tagStyleMap[member.personnelType] ?? styles.tagNeutral}`}
          >
            {personnelTypeLabel}
          </Tag>
        ) : null}

        {member.employmentStatus ? (
          <Tag
            bordered={false}
            className={`${styles.metaTag} ${styles.tagNeutral}`}
          >
            {EMPLOYMENT_STATUS_LABELS[member.employmentStatus]}
          </Tag>
        ) : null}

        {member.status ? (
          <Tag
            bordered={false}
            className={`${styles.metaTag} ${tagStyleMap[member.status] ?? styles.tagNeutral}`}
          >
            {ACCOUNT_STATUS_LABELS[member.status]}
          </Tag>
        ) : null}
      </div>
    );
  };

  const memberEmpNo = (member: Member) => {
    return member.empNo || "暫無工號";
  };

  const renderMemberItem = (member: Member) => {
    const selected = selectedMemberIds.has(member.uid);
    const isDisabled = disabledMemberIds.has(member.uid);
    const disabled = isDisabled || (!selected && isMaxReached);

    return (
      <div
        key={member.uid}
        className={[
          styles.memberCard,
          selected ? styles.memberCardSelected : "",
          disabled ? styles.memberCardDisabled : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => {
          if (!disabled) {
            handleSelectChange(member, !selected);
          }
        }}
      >
        <div className={styles.memberCardInner}>
          <div
            className={styles.selectControl}
            onClick={(event) => event.stopPropagation()}
          >
            {mode === "multiple" ? (
              <Checkbox
                checked={selected}
                disabled={disabled}
                onChange={(event) =>
                  handleSelectChange(member, event.target.checked)
                }
              />
            ) : (
              <Radio
                checked={selected}
                disabled={disabled}
                onChange={(event) =>
                  handleSelectChange(member, event.target.checked)
                }
              />
            )}
          </div>

          <Avatar size={32} className={styles.avatar} icon={<UserOutlined />}>
            {member.name?.charAt(0)}
          </Avatar>

          <div className={styles.memberContent}>
            <div className={styles.memberTopRow}>
              <div className={styles.memberPrimary}>
                <div className={styles.memberName}>{member.name}</div>
                <div className={styles.memberEmpNo}>
                  <IdcardOutlined />
                  <span>{memberEmpNo(member)}</span>
                </div>
              </div>
            </div>

            <div className={styles.memberBottomRow}>
              <div className={styles.teamMeta}>
                <TeamOutlined />
                <span className={styles.teamMetaText}>
                  {member.teamName || "未歸屬團隊"}
                </span>
              </div>

              <div className={styles.memberTags}>{renderMetaTags(member)}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>候選成員</div>
        <div className={styles.countBadge}>已載入 {members.length} 人</div>
      </div>

      <div
        ref={listContainerRef}
        onScroll={handleScroll}
        className={styles.listViewport}
      >
        {loading && members.length === 0 ? (
          <div className={styles.centerState}>
            <Spin />
          </div>
        ) : members.length === 0 ? (
          <div className={styles.emptyState}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchKeyword
                  ? EMPTY_TEXTS.NO_SEARCH_RESULTS
                  : EMPTY_TEXTS.NO_MEMBERS
              }
            />
          </div>
        ) : (
          <div className={styles.listStack}>
            {members.map(renderMemberItem)}

            {loading ? (
              <div className={styles.loadingMore}>
                <Spin size="small" />
                <span>正在載入更多成員...</span>
              </div>
            ) : null}

            {!hasMore ? <div className={styles.endText}>已經到底了</div> : null}
          </div>
        )}
      </div>
    </div>
  );
});

MemberList.displayName = "MemberList";

export default MemberList;
